<template>
  <Teleport to="body">
    <Transition name="cm-backdrop">
      <div
        v-if="open"
        class="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm p-0 sm:p-4"
        @click.self="handleCancel"
      >
        <Transition name="cm-panel" appear>
          <div
            ref="panelRef"
            role="dialog"
            aria-modal="true"
            :aria-labelledby="titleId"
            :aria-describedby="msgId"
            class="w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl shadow-2xl max-h-[90vh] flex flex-col overflow-hidden"
            style="background: var(--color-surface)"
          >
            <!-- Header -->
            <div class="flex items-start gap-3 px-5 pt-5 pb-3">
              <span
                v-if="variant === 'danger'"
                class="shrink-0 grid place-items-center w-10 h-10 rounded-full"
                style="background: color-mix(in srgb, var(--color-danger, #dc2626) 12%, transparent)"
                aria-hidden="true"
              >
                <AlertTriangle :size="20" style="color: var(--color-danger, #dc2626)" />
              </span>
              <h2
                :id="titleId"
                class="flex-1 min-w-0 text-lg font-semibold leading-snug pt-0.5"
                style="color: var(--color-text)"
              >
                {{ title }}
              </h2>
              <AppButton
                variant="ghost"
                class="shrink-0 -mr-1.5 -mt-1 p-1.5"
                aria-label="Cerrar"
                @click="handleCancel"
              >
                <X :size="20" />
              </AppButton>
            </div>

            <!-- Body -->
            <div class="px-5 pb-4 overflow-y-auto">
              <p
                :id="msgId"
                class="text-sm leading-relaxed whitespace-pre-line"
                style="color: var(--color-text-muted)"
              >
                {{ message }}
              </p>
            </div>

            <!-- Footer / actions — stacked on mobile (primary on top), right-aligned on desktop -->
            <div
              class="flex flex-col-reverse sm:flex-row sm:flex-wrap sm:justify-end gap-2 sm:gap-2.5 px-5 py-4"
              style="border-top: 1px solid var(--color-border)"
            >
              <AppButton
                data-cm-cancel
                variant="ghost"
                class="w-full sm:w-auto justify-center min-h-[2.75rem] sm:min-h-0"
                @click="handleCancel"
              >
                {{ cancelLabel }}
              </AppButton>
              <AppButton
                v-if="secondaryAction"
                class="w-full sm:w-auto justify-center min-h-[2.75rem] sm:min-h-0"
                :variant="secondaryAction.variant === 'primary' ? 'primary' : 'secondary'"
                @click="handleSecondary"
              >
                {{ secondaryAction.label }}
              </AppButton>
              <AppButton
                v-if="tertiaryAction"
                class="w-full sm:w-auto justify-center min-h-[2.75rem] sm:min-h-0"
                :variant="tertiaryAction.variant === 'primary' ? 'primary' : 'secondary'"
                @click="handleTertiary"
              >
                {{ tertiaryAction.label }}
              </AppButton>
              <AppButton
                data-cm-primary
                class="w-full sm:w-auto justify-center min-h-[2.75rem] sm:min-h-0"
                :variant="variant === 'danger' ? 'danger' : 'primary'"
                @click="handleConfirm"
              >
                {{ confirmLabel }}
              </AppButton>
            </div>
          </div>
        </Transition>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { onMounted, onBeforeUnmount, watch, ref, nextTick } from 'vue'
import { X, AlertTriangle } from 'lucide-vue-next'
import { useModalHistoryProp } from '~/composables/useModalHistory'
import { useFocusTrap } from '~/composables/useFocusTrap'

interface SecondaryAction {
  label: string
  variant?: 'primary' | 'secondary'
}

interface Props {
  open: boolean
  title: string
  message: string
  confirmLabel?: string
  cancelLabel?: string
  variant?: 'primary' | 'danger'
  secondaryAction?: SecondaryAction
  tertiaryAction?:  SecondaryAction
}

const props = withDefaults(defineProps<Props>(), {
  confirmLabel: 'Confirmar',
  cancelLabel: 'Cancelar',
  variant: 'primary',
})

const emit = defineEmits<{
  confirm: []
  cancel: []
  secondary: []
  tertiary: []
  'update:open': [value: boolean]
}>()

// Unique ids for aria-labelledby / aria-describedby. The modal only renders
// client-side (Teleport + v-if), so a simple sequence avoids hydration concerns.
const _uid = (globalThis as any).__cmSeq = (((globalThis as any).__cmSeq ?? 0) + 1)
const titleId = `cm-title-${_uid}`
const msgId   = `cm-msg-${_uid}`

const panelRef = ref<HTMLElement | null>(null)

function handleConfirm() {
  emit('confirm')
  emit('update:open', false)
}

function handleCancel() {
  emit('cancel')
  emit('update:open', false)
}

function handleSecondary() {
  emit('secondary')
  emit('update:open', false)
}

function handleTertiary() {
  emit('tertiary')
  emit('update:open', false)
}

function onKey(e: KeyboardEvent) {
  if (!props.open) return
  if (e.key === 'Escape') handleCancel()
  else if (e.key === 'Enter') handleConfirm()
}

// Tab never leaks to the page (or to a BaseModal underneath) while the confirm is open.
// Initial focus is handled by the watcher below, not by the trap.
useFocusTrap(panelRef, () => props.open, { initialFocus: false })

// On open, move focus into the dialog: the safe default (Cancelar) for destructive
// actions, the primary action otherwise. Guarded so it never throws (SSR / tests).
watch(() => props.open, (isOpen) => {
  if (!isOpen) return
  nextTick(() => {
    const sel = props.variant === 'danger' ? '[data-cm-cancel]' : '[data-cm-primary]'
    panelRef.value?.querySelector<HTMLElement>(sel)?.focus()
  })
}, { immediate: true })

onMounted(() => window.addEventListener('keydown', onKey))
onBeforeUnmount(() => window.removeEventListener('keydown', onKey))

useModalHistoryProp(() => props.open, handleCancel)
</script>

<style scoped>
.cm-backdrop-enter-active,
.cm-backdrop-leave-active { transition: opacity 0.2s ease; }
.cm-backdrop-enter-from,
.cm-backdrop-leave-to { opacity: 0; }

.cm-panel-enter-active { transition: transform 0.24s cubic-bezier(0.16, 1, 0.3, 1), opacity 0.2s ease; }
.cm-panel-enter-from { opacity: 0; transform: translateY(12px) scale(0.97); }

@media (prefers-reduced-motion: reduce) {
  .cm-backdrop-enter-active,
  .cm-backdrop-leave-active,
  .cm-panel-enter-active { transition: none !important; }
  .cm-panel-enter-from { transform: none; }
}
</style>
